/**
 * Pure scalar constants extracted from `professional.ts`. Kept in a separate
 * file so they can be imported in Hermes/React-Native bundles without dragging
 * the rest of `professional.ts` (which transitively imports `qs` via
 * `filter/utils` and other Node-only modules).
 *
 * `professional.ts` re-exports everything here for back-compat with web/backend
 * callers.
 */
export const FALLBACK_COUNTRY = {
  code: 'FR',
  language: 'fr-FR',
};

export const maxStrLength = 128;

export const maxTagSelectable = 50;
export const maxCatalogCategorySelectable = 100;
export const maxSupplementSelectable = 200;

/* Nombre maximum d'éléments affichés par colonne du footer du site (nav, prestations,
   articles). Source unique : schéma du footer (maxItems), fetch SSR/client ET libellés. */
export const FOOTER_MAX_ITEMS = 6;

/* Nombre maximum d'articles du bloc « Actualités à la une » (sélection manuelle ET
   nombre de derniers articles chargés en automatique). Source unique : schéma du
   composant (maxItems), fetch SSR/client ET libellés. */
export const NEWS_HIGHLIGHT_MAX_ITEMS = 3;

/**
 * Une option de `<select>` des paramètres de réservation : la valeur stockée en base
 * (colonne `Int` de `pro_booking_params`, ou de `prestation` pour une règle propre à une
 * prestation) + la clé de traduction du libellé affiché (namespace `DYNAMIC_FORM` de
 * `translation/fr-FR.ts`).
 */
export type BookingParamOption = { value: number; label: string };

/*
 * ─── Options des paramètres de réservation (`pro_booking_params`) ────────────────
 *
 * SOURCE DE VÉRITÉ UNIQUE. Ses consommateurs en dérivent et ne doivent JAMAIS
 * redéclarer leur propre liste :
 *
 *  1. `UpsertProBookingParametersRequestSchema` (professional.ts) construit ses
 *     `Type.Union` de littéraux à partir d'elles → c'est ce qui alimente le
 *     formulaire de l'onglet « Calendrier » des réglages agenda.
 *  2. Les règles propres d'une prestation : `PrestationMinimumTimeBeforeBookingSchema`
 *     et `PrestationHorizonBookableSchema` (professional.ts) n'acceptent que les valeurs
 *     de `BOOKING_MIN_HOURS_BEFORE_BOOKING` / `BOOKING_HORIZON_BOOKABLE_DAYS` (ou `null`
 *     = hérite), et `BookingRulesBlock.tsx` (frontend-web) en tire ses sélecteurs et
 *     leurs libellés.
 *  3. Le mapper d'import v1 (`migration_v1/mappers/booking_params.mapper.ts`)
 *     « snappe » les valeurs v1 sur la valeur autorisée la plus proche. Une valeur
 *     hors liste rendrait un select sans option sélectionnée (bug historique de la
 *     migration v1).
 *  4. `NEUTRAL_BOOKING_PARAMS` (backend, `pro_establishment/booking-params.defaults.ts`)
 *     prend le plus grand horizon de la liste.
 *
 * Ordre croissant : c'est l'ordre d'affichage dans le select.
 * Les colonnes Prisma sont des `Int` sans contrainte : ajouter une valeur ici ne
 * demande aucune migration, seulement la clé de traduction correspondante.
 * En RETIRER une laisse en revanche orphelines les lignes qui la portent déjà : réglages
 * généraux et, pour le délai minimum et l'horizon, règles propres des prestations
 * (`prestation.minimum_time_before_booking` / `prestation.horizon_bookable`). Le moteur
 * l'applique toujours, mais l'API la refuse en écriture (400), le select du réglage général
 * n'a plus d'option pour elle et BookingRulesBlock l'affiche avec un libellé de repli
 * (« N heures » / « N jours »). Retirer une valeur demande donc d'abord une migration de
 * données qui recale ces lignes sur une valeur autorisée (garde-fou :
 * professional_constants.spec.ts).
 */

/** Délai minimum entre maintenant et le début d'un RDV réservable, en heures. `0` = réservation immédiate. */
export const BOOKING_MIN_HOURS_BEFORE_BOOKING: BookingParamOption[] = [
  { value: 0, label: '0HR' },
  { value: 1, label: '1HR' },
  { value: 2, label: '2HR' },
  { value: 6, label: '6HR' },
  { value: 12, label: '12HR' },
  { value: 24, label: '24HR' },
  { value: 48, label: '48HR' },
  { value: 72, label: '72HR' },
];

/** Délai minimum avant le début du RDV pour qu'un client puisse encore annuler, en heures. `0` = annulable jusqu'au début. */
export const BOOKING_MIN_HOURS_BEFORE_CANCEL: BookingParamOption[] = [
  { value: 0, label: '0HR' },
  { value: 2, label: '2HR' },
  { value: 6, label: '6HR' },
  { value: 12, label: '12HR' },
  { value: 24, label: '24HR' },
  { value: 36, label: '36HR' },
  { value: 48, label: '48HR' },
  { value: 72, label: '72HR' },
  { value: 168, label: '7DAYS' },
];

/** Temps de repos intercalé après chaque RDV, en minutes. `0` = aucun. */
export const BOOKING_REST_BETWEEN_BOOKING_MIN: BookingParamOption[] = [
  { value: 0, label: '0MIN' },
  { value: 5, label: '5MIN' },
  { value: 10, label: '10MIN' },
  { value: 15, label: '15MIN' },
  { value: 20, label: '20MIN' },
  { value: 30, label: '30MIN' },
  { value: 45, label: '45MIN' },
  { value: 60, label: '60MIN' },
];

/** Horizon de réservation, en jours. Doit rester sous `MAX_BOOKING_HORIZON_DAYS` (availabilities.ts). */
export const BOOKING_HORIZON_BOOKABLE_DAYS: BookingParamOption[] = [
  { value: 7, label: '1WEEK' },
  { value: 14, label: '2WEEKS' },
  { value: 30, label: '1MONTH' },
  { value: 60, label: '2MONTHS' },
  { value: 90, label: '3MONTHS' },
  { value: 180, label: '6MONTHS' },
  { value: 365, label: '12MONTHS' },
];

/**
 * Pas de la grille de créneaux proposés au client, en minutes.
 * `0` est un SENTINEL : « pas de pas fixe », la grille se cale alors sur la durée
 * de la prestation demandée (cf. `availability.service.ts`). Ne pas le retirer.
 */
export const BOOKING_STEP_MIN: BookingParamOption[] = [
  { value: 0, label: '0MIN' },
  { value: 5, label: '5MIN' },
  { value: 10, label: '10MIN' },
  { value: 15, label: '15MIN' },
  { value: 20, label: '20MIN' },
  { value: 30, label: '30MIN' },
  { value: 60, label: '60MIN' },
  { value: 120, label: '120MIN' },
];
